/**
 * Datums-Helpers für Frontend (Phase 3.5 §4).
 *
 * 1:1-Logik aus `cotrainer-workers/src/lib/dates.ts` (vereinfacht — ohne
 * Berlin-TZ-Korrektur, weil Browser bei deutschen Usern eh in Europe/Berlin
 * läuft). Wenn jemand mit reise-TZ einloggt und um Mitternacht die ISO-Woche
 * dreht, kommt 1× pro Jahr ein 1-Tag-Versatz — akzeptabel für Phase 3.
 */

export type IsoWeek = { year: number; week: number };

/**
 * ISO-8601 Wochen-Berechnung (Donnerstag der Woche ist immer im Year).
 * Pattern 1:1 zu Worker `getIsoYearWeekBerlin` aber ohne explicit TZ-Konvert.
 */
export function getIsoYearWeek(date: Date): IsoWeek {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // 1=Mo..7=So
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // shift to Thursday
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

export function getCurrentIsoYearWeek(): IsoWeek {
  return getIsoYearWeek(new Date());
}

/** YYYY-MM-DD in Berlin-TZ. */
export function formatBerlinDate(date: Date): string {
  return date.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
}

// -----------------------------------------------------------------------------
// App-weites Anzeige-Datumsformat (System-Setting DATE_FORMAT, Phase 7.4 Item 6).
// Zentraler Helper; Default "de". Globale Anwendung auf alle View-lokalen fmtDate
// ist bewusst NICHT retrofittet (Überoptimierung) — neue/refaktorierte Views
// nutzen appFormatDate. setAppDateFormat wird beim Bootstrap aus dem Setting gefüllt.
// -----------------------------------------------------------------------------
let _appDateFormat: "de" | "iso" = "de";
export function setAppDateFormat(f: "de" | "iso"): void { _appDateFormat = f === "iso" ? "iso" : "de"; }
export function getAppDateFormat(): "de" | "iso" { return _appDateFormat; }

/** Zeigt ein ISO-Datum (YYYY-MM-DD…) gemäß System-Setting an. Unparsebares → roh. */
export function appFormatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso ?? "").trim());
  if (!m) return String(iso ?? "");
  return _appDateFormat === "iso" ? `${m[1]}-${m[2]}-${m[3]}` : `${m[3]}.${m[2]}.${m[1]}`;
}

/** YYYY-MM-DD HH:mm in Berlin-TZ (für UI-Display). */
export function formatBerlinDateTime(date: Date): string {
  return date.toLocaleString("sv-SE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

/** Wochentag-Name (DE) für ein Datum. */
export function germanWeekday(date: Date): string {
  return date.toLocaleDateString("de-DE", { weekday: "long", timeZone: "Europe/Berlin" });
}
